import { EntityBase } from '@provision/entity-types';
import { clone, mapValues, omit } from 'remeda';

/**
 * Recursively omits specified fields from an object.
 *
 * @param {Object} obj - The object to process.
 * @param {Array<string>} fieldsToOmit - The fields to omit in the object.
 * @returns {Object} - The object with specified fields omitted.
 */
function omitDeep<T, U = T extends (infer V)[] ? V : never>(
  obj: T,
  fieldsToOmit: (keyof T)[],
): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => omitDeep(item as T, fieldsToOmit)) as T & U[];
  }

  if (obj && typeof obj === 'object') {
    const newObj = omit(obj, fieldsToOmit);
    return mapValues(newObj, (value) => omitDeep(value, fieldsToOmit)) as T;
  }

  return obj;
}

/**
 * Deep clones an object and omits specified fields.
 *
 * @param {Object} obj - The object to clone.
 * @param {Array<string>} fieldsToOmit - The fields to omit in the cloned object.
 * @returns {Object} - The deep cloned object with specified fields omitted.
 */
export function deepCloneAndOmit<T = Record<string, unknown>>(
  obj: T,
  fieldsToOmit: (keyof T)[],
) {
  return omitDeep<T>(clone(obj), fieldsToOmit);
}

/**
 * Helper function to remove undefined properties from object.
 * We still want to keep boolean or numbers untouched
 * @param {Object} data - Object to update.
 */
export function removeNotDefinedProperties<T extends Record<string, unknown>>(
  data: T,
): Partial<T> {
  return Object.entries(data).reduce<Partial<T>>((acc, [key, value]) => {
    if (value !== undefined) {
      return { ...acc, [key]: value };
    }

    return acc;
  }, {});
}

type NullableValues<T> = { [K in keyof T]: T[K] | null };

/**
 * Returns a shallow copy of the object where any empty-string value is replaced
 * with null. Used so that clearing a text field persists as a true null instead
 * of an empty string.
 * @param {Object} data - Object to normalize.
 */
export function emptyStringsToNull<T extends Record<string, unknown>>(
  data: T,
): NullableValues<T> {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value === '' ? null : value]),
  ) as NullableValues<T>;
}

/**
 * Parses stringified JSON catching all errors
 *
 * @param {Object} obj - The string-object to process.
 * @param {Function} guard - The guard function to validate the parsed value.
 * @returns {T}
 */
export function parseJsonSafely<T>(
  strObj: string,
  guard?: (value: unknown) => value is T,
): T | null {
  try {
    if (!strObj?.trim().length) {
      return null;
    }

    const parsed: unknown = JSON.parse(strObj);
    if (guard && !guard(parsed)) return null;
    return parsed as T;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export const isSameRecords = (recordA: EntityBase, recordB: EntityBase) =>
  recordA.id === recordB.id;

export const toStringArray = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((item): item is string => typeof item === 'string' && !!item)
    : [];
